import { fg } from '@atlaskit/platform-feature-flags/fg';

/**
 * The one definition of the code search gate precedence and scope rules. Consumers bind their
 * own product keys via `getCodeSearchScope` and must not re-implement either.
 *
 * Precedence: the code search gate wins over legacy.
 */

/**
 * Gates the Bitbucket Document type option, the code entity, and the chunked renderer.
 *
 * `rovo_t26_eu_code_search_m2` is not the unrelated `serveFpsMultiSiteSearchExperimentM2`.
 */
export const isCodeSearchEnabled = (): boolean => fg('rovo_t26_eu_code_search_m2');

/** Quick Find serves code in this mode only, so the newer gate must suppress it (QS-10642). */
export const isLegacyCodeSearchEnabled = (): boolean =>
	!fg('rovo_t26_eu_code_search_m2') && fg('rovo_github_code_search');

/** Any mode that can produce a code result, for render and analytics paths. */
export const isAnyCodeSearchEnabled = (): boolean =>
	fg('rovo_t26_eu_code_search_m2') || fg('rovo_github_code_search');

/**
 * Why a search should request code, or `'none'`. Callers need the reason, not a boolean: a code
 * host's filter entry advertises a different entity set per scope.
 *
 * - `'document'` — Document is in the selected type scope.
 * - `'all-types'` — a sole code host with no type filter, so every type that host offers is
 *   requested and the code entity is one of them (QS-10701). The only reachable multi-entity
 *   scope, since the type filter is single-select.
 * - `'legacy-github'` — pre-QS-10634: GitHub is the sole selected product, whatever the type
 *   filter says. Never reached by Bitbucket.
 */
export type CodeSearchScope = 'none' | 'document' | 'all-types' | 'legacy-github';

const DOCUMENT_TYPE_KEY = 'document';

export type GetCodeSearchScopeOptions = {
	/** `getThirdPartyFilters` passes the list `getProducts` collapsed for the request. */
	products: readonly string[];
	/** Selected universal-type filter keys. Empty means all types. */
	selectedTypes: readonly string[];
	/**
	 * Whether no app filter is applied — the only way to tell a selected code host from one
	 * merely available. An All-apps search must never request code: QS-10639 has the aggregator
	 * reject it, so the payload would just disagree with `includeCodeSearch`.
	 */
	isAllProductSearch: boolean;
	/** Package-specific; the connectors config also covers the Forge PIO GitHub connectors. */
	codeHostProductKeys: readonly string[];
	/** The GitHub subset of `codeHostProductKeys`, for the legacy rule. */
	githubProductKeys: readonly string[];
};

export const getCodeSearchScope = ({
	products,
	selectedTypes,
	isAllProductSearch,
	codeHostProductKeys,
	githubProductKeys,
}: GetCodeSearchScopeOptions): CodeSearchScope => {
	if (fg('rovo_t26_eu_code_search_m2')) {
		// Permissive `.some`, so the Relay code fragment is never omitted while a filter entry
		// still carries the entity.
		if (
			products.some((product) => codeHostProductKeys.includes(product)) &&
			selectedTypes.includes(DOCUMENT_TYPE_KEY)
		) {
			return 'document';
		}
		// Strict, so mixing a code host with another product does not pull code in.
		if (
			!isAllProductSearch &&
			selectedTypes.length === 0 &&
			products.length === 1 &&
			codeHostProductKeys.includes(products[0])
		) {
			return 'all-types';
		}
		return 'none';
	}
	if (
		products.length === 1 &&
		githubProductKeys.includes(products[0]) &&
		fg('rovo_github_code_search')
	) {
		return 'legacy-github';
	}
	return 'none';
};
